// src/lib/alerting.ts
// Sentry-compatible alerting configuration for the Insider Performance frontend.
//
// Exports:
//   - ALERT_CONFIG   – declarative thresholds for error-rate and web-vitals alerts
//   - beforeSend     – Sentry `beforeSend` callback that tags alert-worthy events
//   - reportWebVital – helper to flag degraded Core Web Vitals via Sentry tags

import * as Sentry from '@sentry/react';
import { SENTRY_CONFIG } from './config';
import logger from './logger';

// ---------------------------------------------------------------------------
// Alert thresholds (configurable via env, sensible defaults)
// ---------------------------------------------------------------------------

/** Fraction of events that are errors before an alert fires (0–1). */
const ERROR_RATE_THRESHOLD = Number(import.meta.env.VITE_ALERT_ERROR_RATE_THRESHOLD ?? 0.05);

/** Maximum acceptable Largest Contentful Paint in ms. */
const LCP_THRESHOLD_MS = Number(import.meta.env.VITE_ALERT_LCP_THRESHOLD_MS ?? 2500);

/** Maximum acceptable First Input Delay in ms. */
const FID_THRESHOLD_MS = Number(import.meta.env.VITE_ALERT_FID_THRESHOLD_MS ?? 100);

/** Maximum acceptable Cumulative Layout Shift score. */
const CLS_THRESHOLD = Number(import.meta.env.VITE_ALERT_CLS_THRESHOLD ?? 0.1);

/** Maximum acceptable Interaction to Next Paint in ms. */
const INP_THRESHOLD_MS = Number(import.meta.env.VITE_ALERT_INP_THRESHOLD_MS ?? 200);

/** Sliding window size (number of events) for the error-rate tracker. */
const ERROR_WINDOW_SIZE = Number(import.meta.env.VITE_ALERT_ERROR_WINDOW_SIZE ?? 100);

// ---------------------------------------------------------------------------
// Exported configuration object
// ---------------------------------------------------------------------------

export interface AlertRule {
  name: string;
  description: string;
  metric: string;
  threshold: number;
  severity: 'warning' | 'error' | 'critical';
}

export const ALERT_CONFIG = {
  errorRate: {
    threshold: ERROR_RATE_THRESHOLD,
    windowSize: ERROR_WINDOW_SIZE,
  },
  webVitals: {
    lcp: LCP_THRESHOLD_MS,
    fid: FID_THRESHOLD_MS,
    cls: CLS_THRESHOLD,
    inp: INP_THRESHOLD_MS,
  },
  rules: [
    {
      name: 'high-error-rate',
      description: 'Frontend error rate exceeds threshold within the sliding window',
      metric: 'error_rate',
      threshold: ERROR_RATE_THRESHOLD,
      severity: 'critical',
    },
    {
      name: 'lcp-degradation',
      description: 'Largest Contentful Paint exceeds acceptable threshold',
      metric: 'web_vital_lcp',
      threshold: LCP_THRESHOLD_MS,
      severity: 'error',
    },
    {
      name: 'fid-degradation',
      description: 'First Input Delay exceeds acceptable threshold',
      metric: 'web_vital_fid',
      threshold: FID_THRESHOLD_MS,
      severity: 'warning',
    },
    {
      name: 'cls-degradation',
      description: 'Cumulative Layout Shift exceeds acceptable threshold',
      metric: 'web_vital_cls',
      threshold: CLS_THRESHOLD,
      severity: 'warning',
    },
    {
      name: 'inp-degradation',
      description: 'Interaction to Next Paint exceeds acceptable threshold',
      metric: 'web_vital_inp',
      threshold: INP_THRESHOLD_MS,
      severity: 'error',
    },
  ] satisfies AlertRule[],
} as const;

// ---------------------------------------------------------------------------
// Sliding-window error-rate tracker
// ---------------------------------------------------------------------------

class ErrorRateTracker {
  private events: boolean[] = [];
  private readonly windowSize: number;
  private lastAlertIndex = 0;

  constructor(windowSize: number) {
    this.windowSize = windowSize;
  }

  record(isError: boolean): void {
    this.events.push(isError);
    if (this.events.length > this.windowSize) {
      this.events.shift();
    }
  }

  getErrorRate(): number {
    if (this.events.length === 0) return 0;
    const errors = this.events.filter(Boolean).length;
    return errors / this.events.length;
  }

  shouldAlert(threshold: number): boolean {
    if (this.events.length < 10) return false; // need minimum sample
    if (this.getErrorRate() < threshold) return false;
    // Cooldown: only alert once per full window cycle
    if (this.events.length - this.lastAlertIndex < this.windowSize / 2) return false;
    this.lastAlertIndex = this.events.length;
    return true;
  }
}

const tracker = new ErrorRateTracker(ALERT_CONFIG.errorRate.windowSize);

function getAlertActionability(alertRule?: string): 'actionable' | 'informational' {
  if (!alertRule) {
    return 'actionable';
  }
  if (alertRule.endsWith('-degradation')) {
    return 'informational';
  }
  return 'actionable';
}

// ---------------------------------------------------------------------------
// Sentry beforeSend hook
// ---------------------------------------------------------------------------

/**
 * Sentry `beforeSend` callback.
 *
 * Tracks the rolling error rate and enriches alert-worthy events with
 * custom tags so Sentry issue alerts can route on `alert_rule`.
 *
 * Usage – pass to `Sentry.init({ beforeSend })` or compose with other hooks.
 */
export function beforeSend(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): Sentry.ErrorEvent | null {
  const isError = event.level === 'error' || event.level === 'fatal';
  tracker.record(isError);

  const existingAlertRule =
    typeof event.tags?.alert_rule === 'string' ? String(event.tags.alert_rule) : undefined;
  const actionability = getAlertActionability(existingAlertRule);
  event.tags = {
    ...event.tags,
    signal_surface: event.tags?.signal_surface ?? 'frontend',
    alert_actionability: event.tags?.alert_actionability ?? actionability,
  };

  if (isError && tracker.shouldAlert(ALERT_CONFIG.errorRate.threshold)) {
    const rate = tracker.getErrorRate();
    event.tags = {
      ...event.tags,
      alert_rule: 'high-error-rate',
      alert_severity: 'critical',
      error_rate: `${(rate * 100).toFixed(1)}%`,
    };
    event.contexts = {
      ...event.contexts,
      alert: {
        error_rate: rate,
        threshold: ALERT_CONFIG.errorRate.threshold,
        window_size: ALERT_CONFIG.errorRate.windowSize,
        original_exception:
          hint.originalException instanceof Error ? hint.originalException.message : undefined,
      },
    };
    logger.error(
      `[alerting] High error rate detected: ${(rate * 100).toFixed(1)}% ` +
        `(threshold ${(ALERT_CONFIG.errorRate.threshold * 100).toFixed(1)}%)`
    );
  }

  return event;
}

// ---------------------------------------------------------------------------
// Web Vitals alerting helper
// ---------------------------------------------------------------------------

interface WebVitalEntry {
  name: string;
  value: number;
}

/**
 * Report a Core Web Vital measurement and tag it as degraded if it exceeds
 * the configured threshold.
 *
 * Call from a `web-vitals` observer or `Sentry.metrics`:
 * ```ts
 * import { onLCP, onFID, onCLS, onINP } from 'web-vitals';
 * import { reportWebVital } from './alerting';
 * onLCP(reportWebVital);
 * ```
 */
export function reportWebVital(entry: WebVitalEntry): void {
  const thresholds: Record<string, number> = {
    LCP: ALERT_CONFIG.webVitals.lcp,
    FID: ALERT_CONFIG.webVitals.fid,
    CLS: ALERT_CONFIG.webVitals.cls,
    INP: ALERT_CONFIG.webVitals.inp,
  };

  const limit = thresholds[entry.name];
  if (limit === undefined) return;

  const isDegraded = entry.value > limit;
  if (!isDegraded) {
    return;
  }

  logger.warn(`[alerting] ${entry.name} degraded: ${entry.value} exceeds threshold ${limit}`);

  if (SENTRY_CONFIG.enabled && SENTRY_CONFIG.captureWebVitalIssues) {
    Sentry.withScope((scope) => {
      scope.setTag('alert_rule', `${entry.name.toLowerCase()}-degradation`);
      scope.setTag('alert_actionability', 'informational');
      scope.setTag('signal_surface', 'web_vital');
      scope.setTag(
        'alert_severity',
        entry.name === 'CLS' || entry.name === 'FID' ? 'warning' : 'error'
      );
      scope.setContext('web_vital', {
        metric: entry.name,
        value: entry.value,
        threshold: limit,
      });

      Sentry.captureMessage(
        `Web Vital ${entry.name} degraded: ${entry.value} (threshold ${limit})`,
        'warning'
      );
    });
  }
}
